import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  User, Lock, CreditCard, Package, Check, AlertCircle, Loader2,
  ChevronRight, ExternalLink
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'purchases', label: 'Purchase History', icon: CreditCard },
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div className="py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-bastion-black mb-6">Settings</h1>

        {/* Tab navigation */}
        <div className="flex gap-1 mb-8 border-b border-bastion-gray-200">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-bastion-black text-bastion-black'
                    : 'border-transparent text-bastion-gray-500 hover:text-bastion-gray-700'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'profile' && <ProfileSection />}
        {activeTab === 'purchases' && <PurchaseHistorySection />}
      </div>
    </div>
  );
}

function ProfileSection() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileError(null);
    setProfileSuccess(false);

    try {
      await api.put('/auth/profile', { name, email });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    setPasswordSaving(true);
    setPasswordError(null);
    setPasswordSuccess(false);

    try {
      await api.put('/auth/password', { currentPassword, newPassword });
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Profile Info */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-bastion-black rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Profile Information</h2>
            <p className="text-sm text-bastion-gray-700">Update your name and email address</p>
          </div>
        </div>

        <form onSubmit={handleProfileSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-bastion-gray-800 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-bastion-gray-300 focus:border-bastion-black focus:ring-1 focus:ring-bastion-black outline-none transition-colors"
              placeholder="Your name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bastion-gray-800 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-bastion-gray-300 focus:border-bastion-black focus:ring-1 focus:ring-bastion-black outline-none transition-colors"
              placeholder="your@email.com"
            />
          </div>

          {profileError && (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              {profileError}
            </div>
          )}
          {profileSuccess && (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <Check className="w-4 h-4" />
              Profile updated successfully
            </div>
          )}

          <button
            type="submit"
            disabled={profileSaving}
            className="btn-primary"
          >
            {profileSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save Changes
          </button>
        </form>
      </div>

      {/* Change Password */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-bastion-gray-200 rounded-full flex items-center justify-center">
            <Lock className="w-5 h-5 text-bastion-gray-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Change Password</h2>
            <p className="text-sm text-bastion-gray-700">Update your account password</p>
          </div>
        </div>

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-bastion-gray-800 mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-bastion-gray-300 focus:border-bastion-black focus:ring-1 focus:ring-bastion-black outline-none transition-colors"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bastion-gray-800 mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-bastion-gray-300 focus:border-bastion-black focus:ring-1 focus:ring-bastion-black outline-none transition-colors"
              placeholder="At least 6 characters"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-bastion-gray-800 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-bastion-gray-300 focus:border-bastion-black focus:ring-1 focus:ring-bastion-black outline-none transition-colors"
              required
            />
          </div>

          {passwordError && (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              {passwordError}
            </div>
          )}
          {passwordSuccess && (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <Check className="w-4 h-4" />
              Password changed successfully
            </div>
          )}

          <button
            type="submit"
            disabled={passwordSaving}
            className="btn-primary"
          >
            {passwordSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Update Password
          </button>
        </form>
      </div>

      {/* Credits & Billing Quick Info */}
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Package className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Credits & Billing</h2>
            <p className="text-sm text-bastion-gray-700">Manage your analysis credits</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-bastion-gray-50 rounded-lg">
          <div>
            <p className="text-sm text-bastion-gray-700">Available Credits</p>
            <p className="text-2xl font-bold text-bastion-black">{user?.credits || 0}</p>
          </div>
          <Link to="/pricing" className="btn-outline text-sm py-2 px-4">
            Buy More Credits
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        <p className="text-xs text-bastion-gray-500 mt-3">
          Payments are securely processed by Stripe. We do not store your payment information.
        </p>
      </div>
    </div>
  );
}

function PurchaseHistorySection() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        const res = await api.get('/payments/history');
        setPurchases(res.data.purchases);
      } catch (err) {
        console.error('Failed to fetch purchases:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPurchases();
  }, []);

  if (loading) {
    return (
      <div className="card text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-bastion-gray-400 mx-auto" />
      </div>
    );
  }

  if (purchases.length === 0) {
    return (
      <div className="card text-center py-12">
        <CreditCard className="w-12 h-12 text-bastion-gray-300 mx-auto mb-3" />
        <p className="text-bastion-gray-700 font-medium mb-1">No purchases yet</p>
        <p className="text-bastion-gray-500 text-sm mb-4">
          Purchase analysis credits to get started
        </p>
        <Link to="/pricing" className="btn-primary inline-flex">
          View Pricing
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-4">Purchase History</h2>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-bastion-gray-200">
              <th className="text-left py-3 px-2 font-semibold text-bastion-gray-700">Date</th>
              <th className="text-left py-3 px-2 font-semibold text-bastion-gray-700">Package</th>
              <th className="text-center py-3 px-2 font-semibold text-bastion-gray-700">Credits</th>
              <th className="text-right py-3 px-2 font-semibold text-bastion-gray-700">Amount</th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((p) => (
              <tr key={p.id} className="border-b border-bastion-gray-100 last:border-0">
                <td className="py-3 px-2 text-bastion-gray-800">
                  {new Date(p.date).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric'
                  })}
                </td>
                <td className="py-3 px-2 font-medium text-bastion-black">{p.package}</td>
                <td className="py-3 px-2 text-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    +{p.credits}
                  </span>
                </td>
                <td className="py-3 px-2 text-right font-medium text-bastion-black">
                  ${p.amount.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-3">
        {purchases.map((p) => (
          <div key={p.id} className="bg-bastion-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-bastion-black">{p.package}</span>
              <span className="font-semibold text-bastion-black">${p.amount.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-bastion-gray-700">
                {new Date(p.date).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric'
                })}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                +{p.credits} credits
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
